import { useState } from "react";
import { Mail, Send, Check } from "lucide-react";
import { contactAPI } from "../api";
import MarketingNav from "../components/MarketingNav";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";
import Card from "../components/atoms/Card";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("General");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Name, email, and message are all required.");
      return;
    }
    setLoading(true); setError("");
    try {
      await contactAPI.submit({ name, email, subject: topic, message });
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to send.");
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setSent(false);
    setName(""); setEmail(""); setMessage(""); setTopic("General"); setError("");
  }

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "72px 24px 96px" }}>
        <Chip tone="gold">Contact</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 12px", letterSpacing: "-0.025em" }}>
          Tell us what's <em style={{ color: "var(--gold-500)" }}>working.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 17, maxWidth: "56ch", lineHeight: 1.6, margin: "0 0 40px" }}>
          What broke? What helped? What feature do you wish existed? We read every message and most replies come back the same business day.
        </p>

        {sent ? (
          <Card paperDeep spineColor="var(--sage)" style={{ maxWidth: 560 }}>
            <Check size={36} strokeWidth={1.5} color="var(--sage-dark)" />
            <h2 className="font-display" style={{ fontSize: 32, fontWeight: 400, margin: "12px 0 6px" }}>
              Message sent.
            </h2>
            <p style={{ color: "var(--fg-2)", fontSize: 15, lineHeight: 1.6, margin: "0 0 20px" }}>
              We'll come back within one business day — usually the same day in Indian working hours.
            </p>
            <Button variant="ghost" onClick={reset}>Send another</Button>
          </Card>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: "1fr 2fr", gap: 32 }}>

            <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card>
                <Mail size={20} strokeWidth={1.5} color="var(--gold-600)" />
                <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, margin: "10px 0 4px" }}>Email us</h3>
                <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0, fontFamily: "var(--font-mono)" }}>support@speakeasy.app</p>
              </Card>
              <Card paperDeep>
                <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, margin: "0 0 4px", fontStyle: "italic" }}>A quick note.</h3>
                <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0, lineHeight: 1.55 }}>
                  Bug? Include the page URL and what you clicked. We jump on those first.
                </p>
              </Card>
              <Card paperDeep>
                <h3 className="font-display" style={{ fontSize: 22, fontWeight: 400, margin: "0 0 4px", fontStyle: "italic" }}>Hours.</h3>
                <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0, lineHeight: 1.55 }}>
                  Mon–Fri, 10am–7pm IST. Weekends, slower.
                </p>
              </Card>
            </aside>

            <Card>
              <h2 className="font-display" style={{ fontSize: 28, fontWeight: 400, margin: "0 0 18px" }}>Send a message.</h2>

              {error && (
                <div style={{ background: "#fdf0f1", border: "1px solid #ddb1b6", color: "var(--berry-dark)", borderRadius: 8, padding: "10px 14px", fontSize: 14, marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Field id="name" label="Your name">
                  <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vinothkumar" style={input} />
                </Field>
                <Field id="email" label="Email">
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={input} />
                </Field>
                <Field id="topic" label="Topic">
                  <select id="topic" value={topic} onChange={(e) => setTopic(e.target.value)} style={input}>
                    <option>General</option>
                    <option>Bug report</option>
                    <option>Billing</option>
                    <option>Feature request</option>
                    <option>Partnership / press</option>
                  </select>
                </Field>
                <Field id="message" label="Message">
                  <textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What's on your mind?" rows={6} style={{ ...input, resize: "vertical", minHeight: 120 }} />
                </Field>
                <div>
                  <Button type="submit" variant="primary" disabled={loading} icon={<Send size={16} strokeWidth={1.8} />}>
                    {loading ? "Sending…" : "Send message"}
                  </Button>
                </div>
              </form>
            </Card>

          </div>
        )}
      </section>

      <footer style={{ background: "var(--ink-900)", color: "var(--paper)", padding: "32px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ fontSize: 13, color: "rgba(251,246,232,0.55)", textAlign: "center" }}>
          © {new Date().getFullYear()} SpeakEasy.
        </div>
      </footer>
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--fg-1)", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const input = {
  width: "100%",
  padding: "12px 14px",
  border: "1px solid var(--border-2)",
  borderRadius: "var(--radius-xs)",
  background: "#fff",
  color: "var(--fg-1)",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  outline: "none",
  transition: "border-color 150ms cubic-bezier(.2,.8,.2,1)",
};
